import { useState, useEffect } from 'react';
import { Check, X, Search, Plus, Minus } from 'lucide-react';
import { supabase } from '../services/supabase';
import { logger } from '../services/logger';

interface CoachingTopic {
  id: string;
  name: string;
  description: string;
  category: string;
  topic_type: string;
  icon: string;
}

interface TopicManagerProps {
  onClose: () => void;
  onSave?: () => void;
}

export function TopicManager({ onClose, onSave }: TopicManagerProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allTopics, setAllTopics] = useState<CoachingTopic[]>([]);
  const [enabledTopics, setEnabledTopics] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: topics } = await supabase
        .from('coaching_topics')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      setAllTopics(topics || []);

      const { data: userTopics } = await supabase
        .from('user_coaching_topics')
        .select('coaching_topic_id, is_enabled')
        .eq('user_id', user.id);

      const enabled = new Set<string>(
        (userTopics || [])
          .filter((ut: any) => ut.is_enabled)
          .map((ut: any) => ut.coaching_topic_id)
      );
      setEnabledTopics(enabled);
    } catch (err) {
      logger.error('Error loading topics:', err);
    } finally {
      setLoading(false);
    }
  }

  function toggleTopic(topicId: string) {
    const newEnabled = new Set(enabledTopics);
    if (newEnabled.has(topicId)) {
      newEnabled.delete(topicId);
    } else {
      newEnabled.add(topicId);
    }
    setEnabledTopics(newEnabled);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const upserts = allTopics.map(topic => ({
        user_id: user.id,
        coaching_topic_id: topic.id,
        is_enabled: enabledTopics.has(topic.id)
      }));

      const { error } = await supabase
        .from('user_coaching_topics')
        .upsert(upserts, { onConflict: 'user_id,coaching_topic_id' });

      if (error) throw error;

      if (onSave) onSave();
      onClose();
    } catch (err) {
      logger.error('Error saving topics:', err);
    } finally {
      setSaving(false);
    }
  }

  const categories = ['all', ...Array.from(new Set(allTopics.map(t => t.category)))];

  const filteredTopics = allTopics.filter(topic => {
    const matchesSearch = topic.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         topic.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || topic.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const groupedTopics = filteredTopics.reduce((acc, topic) => {
    if (!acc[topic.category]) {
      acc[topic.category] = [];
    }
    acc[topic.category].push(topic);
    return acc;
  }, {} as Record<string, CoachingTopic[]>);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-500 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-heading font-bold text-ink-950">
                Manage Your Coaching Topics
              </h2>
              <p className="text-gray-600 mt-1">
                Choose which topics you want to see in your coaching hub
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search topics..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
              />
            </div>

            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {cat === 'all' ? 'All Categories' : cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-4 flex items-center justify-between text-sm">
            <span className="text-gray-600">
              {enabledTopics.size} of {allTopics.length} topics enabled
            </span>
            <div className="flex space-x-2">
              <button
                onClick={() => setEnabledTopics(new Set(allTopics.map(t => t.id)))}
                className="text-gold-500 hover:text-gold-600 font-medium"
              >
                <Plus className="h-4 w-4 inline mr-1" />
                Enable All
              </button>
              <button
                onClick={() => setEnabledTopics(new Set())}
                className="text-gray-600 hover:text-gray-700 font-medium"
              >
                <Minus className="h-4 w-4 inline mr-1" />
                Disable All
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {Object.keys(groupedTopics).length === 0 ? (
            <div className="text-center text-gray-500 py-12">
              <p>No topics found matching your search.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedTopics).map(([category, topics]) => (
                <div key={category}>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">
                    {category}
                  </h3>
                  <div className="space-y-2">
                    {topics.map(topic => {
                      const isEnabled = enabledTopics.has(topic.id);

                      return (
                        <button
                          key={topic.id}
                          onClick={() => toggleTopic(topic.id)}
                          className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                            isEnabled
                              ? 'border-gold-500 bg-gold-500/10'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-1">
                                <span className="font-semibold text-gray-900">
                                  {topic.name}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {topic.topic_type === 'both' ? 'Chat & Prompts' :
                                   topic.topic_type === 'conversation' ? 'Chat Only' : 'Prompts Only'}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600">{topic.description}</p>
                            </div>
                            {isEnabled ? (
                              <Check className="h-5 w-5 text-gold-500 flex-shrink-0 ml-2" />
                            ) : (
                              <div className="h-5 w-5 border-2 border-gray-300 rounded flex-shrink-0 ml-2" />
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-white font-medium"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-gold-500 text-ink-950 rounded-lg hover:bg-gold-500/90 disabled:opacity-50 font-semibold"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-ink-950 border-t-transparent inline-block mr-2" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
